import CheckIcon from "@mui/icons-material/Check";
import { Menu, MenuItem, Typography } from "@mui/material";
import React, { type Ref, useCallback, useMemo, useState } from "react";

import { FilterChip } from "~/components/UI/FilterChip";
import { FilterMenuTitle } from "~/components/UI/FilterMenuTitle";
import { RichTooltip } from "~/components/UI/RichTooltip";
import type { IOption } from "~/types/option";

import * as Styled from "./FilterDropdown.styles";

export interface FilterDropdownProps<TValue = string> {
  label: string;
  icon?: React.ReactNode;
  tooltip?: string;
  /** Subscription-locked: gold chip; the info tooltip is suppressed. */
  locked?: boolean;
  options: IOption<TValue>[];
  defaultValue?: TValue;
  value: TValue | null;
  valueLabel?: string | null;
  chipRef?: Ref<HTMLButtonElement>;
  onChange: (value?: TValue) => void;
}

export const FilterDropdown = <TValue,>({
  label,
  icon,
  tooltip,
  locked,
  options,
  defaultValue,
  value,
  valueLabel,
  chipRef,
  onChange,
}: FilterDropdownProps<TValue>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const isDefaultSelected = value === defaultValue;
  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) || null,
    [options, value]
  );

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    (nextValue: TValue) => {
      onChange(nextValue);
      handleClose();
    },
    [handleClose, onChange]
  );

  const handleClear = useCallback(() => onChange(undefined), [onChange]);

  const isApplied = Boolean(selectedOption) && !isDefaultSelected;

  const chipIcon = useMemo(() => {
    if (isApplied && selectedOption?.icon) {
      const IconComponent = selectedOption.icon;
      return <IconComponent fontSize="inherit" />;
    }

    return icon;
  }, [icon, isApplied, selectedOption]);

  const chip = (
    <FilterChip
      ref={chipRef}
      label={label}
      icon={chipIcon}
      valueLabel={isApplied ? valueLabel ?? selectedOption?.label : undefined}
      defaultValueLabel={
        options.find((option) => option.value === defaultValue)?.label
      }
      open={open}
      locked={locked}
      onClick={handleClick}
      onClear={handleClear}
    />
  );

  return (
    <>
      {tooltip && !locked ? (
        <RichTooltip title={tooltip} maxWidth={280}>
          {chip}
        </RichTooltip>
      ) : (
        chip
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: { minWidth: Math.max(anchorEl?.offsetWidth ?? 0, 160) },
          },
        }}
      >
        <FilterMenuTitle sx={{ px: 2, pt: 0.5, pb: 1 }}>
          {label}
        </FilterMenuTitle>
        {options.map((option) => {
          const IconComponent = option.icon;
          const isSelected = value === option.value;
          return (
            <MenuItem
              key={option.value?.toString()}
              onClick={() => handleSelect(option.value)}
              selected={isSelected}
              sx={{ gap: 1, justifyContent: "space-between" }}
            >
              <Styled.MenuItemContent>
                {IconComponent && <IconComponent fontSize="small" />}
                <Typography variant="caption">{option.label}</Typography>
              </Styled.MenuItemContent>
              <CheckIcon
                sx={{
                  fontSize: 16,
                  visibility: isSelected ? "visible" : "hidden",
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
